"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, Tag, ArrowRight, Share2 } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";



function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="18592" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-80 animate-pulse rounded-lg bg-[#4A5D23]/5" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="group overflow-hidden border-none bg-[#F9F9F9] shadow-sm transition-all hover:shadow-md" data-index={i}>
          <div className="aspect-video w-full overflow-hidden bg-[#FAF6F1]">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#FAF6F1] text-[#666666]">
                <span className="text-sm">Kein Bild verfügbar</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs text-[#666666]">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Aktuell"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold leading-tight text-[#4A5D23] transition-colors group-hover:text-[#2C5F63]">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm text-[#666666]">
              {post.excerpt ? (
                <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Erfahren Sie mehr über dieses Thema..."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="text-sm font-medium text-[#2C5F63] hover:underline">
              Beitrag lesen
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPost }: { currentPost: Post }) {
  
  
  const categoryId = currentPost.categories?.[0]?.id;
  
  const relatedParams: Record<string, any> = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPost.id],
    orderby: "date",
    order: "DESC",
  };

  if (categoryId) {
    relatedParams.tax_query = [
      {
        taxonomy: "category",
        field: "term_id",
        terms: categoryId,
      },
    ];
  }

  const wp_query = new WP_Query(relatedParams);

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-bold tracking-tight text-[#4A5D23] md:text-3xl">Weitere Einblicke</h3>
        <Link to="/HomePage">
          <Button variant="ghost" className="hidden text-[#2C5F63] hover:bg-[#4A5D23]/10 sm:flex">
            Alle Beiträge <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsList />
      </PostsProvider>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-lg bg-[#4A5D23]/5" />
        <div className="mb-12 h-96 w-full animate-pulse rounded-lg bg-[#4A5D23]/5" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-[#4A5D23]/5" />
          <div className="h-4 w-full animate-pulse rounded bg-[#4A5D23]/5" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-[#4A5D23]/5" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#4A5D23]">Fehler beim Laden</h2>
        <p className="mt-2 text-[#666666]">Der gewünschte Beitrag konnte nicht geladen werden. Bitte versuchen Sie es später erneut.</p>
        <Link to="/HomePage">
          <Button className="mt-6 bg-[#2C5F63] text-white hover:bg-[#2C5F63]/90">Zur Startseite</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#4A5D23]">Beitrag nicht gefunden</h2>
        <p className="mt-2 text-[#666666]">Der gesuchte Beitrag existiert leider nicht.</p>
        <Link to="/HomePage">
          <Button className="mt-6 bg-[#2C5F63] text-white hover:bg-[#2C5F63]/90">Zur Startseite</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Tai Chi Schule Soonwald Team";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }) : "";

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          {post.categories?.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="rounded-full bg-[#4A5D23]/10 px-3 py-1 text-xs font-medium tracking-wider text-[#4A5D23] uppercase"
            >
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-[#4A5D23] sm:text-4xl md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#666666]">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 border border-[#E0E0E0]">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-[#4A5D23]/10 text-[#4A5D23]"><User className="h-4 w-4" /></AvatarFallback>
            </Avatar>
            <span className="font-medium text-[#333333]">{authorName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{publishDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>5 Min. Lesezeit</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-md md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg prose-neutral dark:prose-invert max-w-none 
            [&_h2]:mt-10 [&_h2]:mb-6 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-[#4A5D23]
            [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-[#4A5D23]
            [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-[#333333]
            [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_li]:text-[#333333]
            [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6
            [&_blockquote]:border-l-4 [&_blockquote]:border-[#4A5D23] [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-[#333333]
            [&_img]:rounded-lg [&_img]:shadow-sm [&_img]:my-8"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-2 border-t border-[#E0E0E0] pt-8">
            <Tag className="mr-2 h-4 w-4 text-[#666666]" />
            {post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"} className="no-underline">
                <span 
                  data-index={i}
                  className="rounded-md bg-[#F9F9F9] px-2.5 py-1 text-sm text-[#666666] transition-colors hover:bg-[#4A5D23]/10 hover:text-[#4A5D23]"
                >
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {}
        <div className="mt-12 rounded-lg border border-[#E0E0E0] bg-[#F9F9F9] p-6 md:p-8 shadow-sm">
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <Avatar className="h-20 w-20 border-2 border-[#FAF6F1] shadow-sm">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-[#4A5D23]/10 text-[#4A5D23] text-lg"><User className="h-8 w-8" /></AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-xl font-bold text-[#333333]">Über {authorName}</h4>
                <div className="mt-2 flex gap-2 sm:mt-0">
                  <Button variant="outline" size="sm" className="h-8 border-[#2C5F63] text-[#2C5F63] hover:bg-[#2C5F63]/10">Folgen</Button>
                </div>
              </div>
              <p className="text-[#333333]">
                Lehrerin der Tai Chi Schule Soonwald und Schülerin von Meister Zhang Xiao Ping. Sie vermittelt die Kunst der inneren Bewegung in der Tradition der alten Meister und schöpft Kraft aus der Stille des Soonwalds.
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-lg bg-[#4A5D23]/5 px-6 py-4 sm:flex-row">
          <span className="font-medium text-[#333333]">Diesen Beitrag teilen:</span>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="rounded-full hover:text-[#2C5F63] hover:border-[#2C5F63]/30">
              <Share2 className="h-4 w-4" />
              <span className="sr-only">Teilen</span>
            </Button>
            <Button variant="outline" size="icon" className="rounded-full hover:text-[#2C5F63] hover:border-[#2C5F63]/30">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
            </Button>
            <Button variant="outline" size="icon" className="rounded-full hover:text-[#2C5F63] hover:border-[#2C5F63]/30">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" /></svg>
            </Button>
          </div>
        </div>
      </div>

      <Separator className="my-16 bg-[#E0E0E0]" />

      {}
      <RelatedPostsWrapper currentPost={post} />
    </article>
  );
}

export default function SinglePostTemplate() {
  
  
  const current_wp_query_params = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-post-template-section" className="bg-[#FAF6F1] text-[#333333]">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}